import { useNavigate } from 'react-router-dom'
import { AccountInfo, useAccount } from '../../../context/account_context'
import { BranchInfo, useBranch } from '../../../context/branch_context'
import { CashierInfo } from '../../../context/cashier_context'
import { useLoading } from '../../../context/loading_context'
import { useSnack } from '../../../context/snack_context'
import { useEffect, useRef, useState } from 'react'
import { SystemBranchEnum, useConfigs } from '../../../context/configs_context'
import {
    generateGeneralData,
    listDocumentTypes,
    listDocuments,
} from '../../../services/document'
import {
    CustomizeDateModal,
    DateFilterOptions,
    DateFilterOptionsLabels,
    getPresetDate,
} from '../../../components/DateFilterPicker'
import { Box, Link, Stack, Tooltip, Typography } from '@mui/material'
import { GenericTable } from '../../../components/GenericTable'
import {
    getDateInDDMMYYYYFormat,
    getDateInIsoFormat,
    getDateString,
    getTimeAgo,
} from '../../../utils/maks'
import { downloadFile, getFileEndpoint } from '../../../services/media'
import GenericModal from '../../../components/GenericModal'
import { listAccounts } from '../../../services/account'
import { useTranslation } from 'react-i18next'
import { useCoin } from '../../../context/coin_context'

export interface DocumentTypeInfo {
    id: number
    name: string
}

export interface DocumentInfo {
    id: number
    date: string
    hour: string
    isSummerTime: boolean
    documentPath: string
    documentState: number
    documentType: DocumentTypeInfo
    branch: BranchInfo | null
    cashier?: Partial<CashierInfo>
    account?: Partial<AccountInfo>
}

export enum DocumentState {
    Pending = 0,
    Completed = 1,
    Canceled = 2,
    Error = 3,
}

export const DocumentStateLabel: Record<number, string> = {
    0: 'Pendiente',
    1: 'Completo',
    2: 'Cancelado',
    3: 'Error',
}

export interface DocumentTableFilters {
    page: number
    rows: number
    rowsCount: number
    documentTypes: DocumentTypeInfo[]
    branches: BranchInfo[]
    date: {
        iniD: Date
        endD: Date
    } | null
    accounts: AccountInfo[]
}

export const DocumentHome = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const loading = useLoading()
    const snack = useSnack()
    const [documents, setDocuments] = useState<DocumentInfo[]>([])
    const [selectedDocuments, setSelectedDocuments] = useState<DocumentInfo[]>(
        []
    )
    const [filters, setFilters] = useState<DocumentTableFilters>({
        page: 1,
        rows: 10,
        rowsCount: 0,
        documentTypes: [],
        branches: [],
        date: null,
        accounts: []
    })
    const [search, setSearch] = useState('')
    const timeoutRef = useRef<NodeJS.Timeout>()
    const { accountInfo, accountRules } = useAccount()
    const [openDateModal, setOpenDateModal] = useState(false)
    const { configs } = useConfigs()
    const { branches } = useBranch()
    const [documentTypes, setDocumentTypes] = useState<DocumentTypeInfo[]>([])
    const [detailedDocument, setDetailedDocument] =
        useState<DocumentInfo | null>(null)
    const [openGeneralDataModal, setOpenGeneralDataModal] = useState(false)
    const [accounts, setAccounts] = useState<AccountInfo[]>([])
    const { coins } = useCoin()

    const searchAccounts = async (value: string) => {
        if (!value || !accountRules.length) return
        try {
            const res = await listAccounts(
                {
                    page: 1,
                    rows: 10,
                    search: value,
                },
                accountRules,
                coins
            )
            setAccounts(res.data)
        } catch (error) {}
    }

    useEffect(() => {
        if(!accountInfo) return

        setFilters({
            ...filters,
            accounts: [accountInfo]
        })
        getDocuments(
            {
                ...filters,
                accounts: [accountInfo],
                page: 1,
            },
            search
        )
        getDocumentTypes()
    }, [accountInfo])

    const getDocuments = async (
        filters: DocumentTableFilters,
        search: string,
        dontSetEmpty?: boolean
    ) => {
        loading.logicShow()
        try {
            const res = await listDocuments({
                skip: filters.page,
                take: filters.rows,
                search: search.length ? search : undefined,
                id_tipo_documento: filters.documentTypes.map((b) => b.id ?? 0),
                id_usuario: filters.accounts.map((b) => b.id ?? 0),
                id_filial: filters.branches.map((b) => b.id ?? 0),
                data_inicial: filters.date
                    ? getDateInIsoFormat(filters.date.iniD)
                    : undefined,
                data_final: filters.date
                    ? getDateInIsoFormat(filters.date.endD)
                    : undefined,
            })
            if (!dontSetEmpty || res.data.length) {
                setFilters({
                    ...filters,
                    rowsCount: res.count,
                })
                setDocuments(res.data)
            }
        } catch (error) {
            console.log(error)
            snack.connectionFail(error)
        }
        loading.logicHide()
    }

    const getDocumentTypes = async () => {
        loading.logicShow()
        try {
            const res = await listDocumentTypes()
            setDocumentTypes(res)
        } catch (error) {
            snack.connectionFail(error)
        }
        loading.logicHide()
    }

    const branchColumn =
        !accountInfo?.branch && configs.systemBranch === SystemBranchEnum.Branch
            ? [
                  {
                      name: 'Sucursal',
                      property: ['branch'],
                      parser: (value: any) => value?.name ?? '---',
                      hideXs: true,
                  },
              ]
            : []

    const handleGenerateGeneralData = async (iniD: Date, endD: Date) => {
        loading.logicShow()
        try {
            await generateGeneralData({
                data_inicial: getDateInIsoFormat(iniD),
                data_final: getDateInIsoFormat(endD),
            })
            getDocuments(filters, search)
            loading.showDocument()
        } catch (error) {
            snack.connectionFail(error)
        }
        loading.logicHide()
    }

    const canGenerateGeneralDocument =
        accountInfo?.isMaster ||
        !!accountInfo?.permissions?.includes('Documentos:Gerar relatório geral')

    return (
        <>
            <GenericModal
                open={Boolean(detailedDocument)}
                handleClose={() => setDetailedDocument(null)}
                title="Visualizar"
                textButton="Baixar"
                handleSubmit={() => {
                    downloadFile(detailedDocument?.documentPath ?? '')
                    setDetailedDocument(null)
                }}
                fullWidth
            >
                <Box
                    component="embed"
                    width={'100%'}
                    src={getFileEndpoint(detailedDocument?.documentPath ?? '')}
                />
            </GenericModal>
            <CustomizeDateModal
                open={openGeneralDataModal}
                handleClose={setOpenGeneralDataModal}
                returnDates={handleGenerateGeneralData}
            />
            <CustomizeDateModal
                open={openDateModal}
                handleClose={setOpenDateModal}
                returnDates={(iniD, endD) => {
                    setFilters({
                        ...filters,
                        date: {
                            iniD: iniD,
                            endD: endD,
                        },
                        page: 1,
                    })
                    getDocuments(
                        {
                            ...filters,
                            date: {
                                iniD: iniD,
                                endD: endD,
                            },
                            page: 1,
                        },
                        search
                    )
                }}
            />
            <Box sx={{ display: 'block' }}>
                <GenericTable
                    title="9f912311-165b-4578-bce7-746f021fe697"
                    filters={[
                        {
                            name: 'Tipo de documento',
                            labelField: 'name',
                            valueField: 'id',
                            values: documentTypes,
                            selectedValues: filters.documentTypes,
                        },
                        {
                            name: 'Usuário',
                            labelField: 'name',
                            valueField: 'id',
                            values: accounts,
                            selectedValues: filters.accounts,
                            autoComplete: {
                                cb: (v) => {
                                    clearTimeout(timeoutRef.current)
                                    timeoutRef.current = setTimeout(() => {
                                        searchAccounts(v)
                                    }, 500)
                                },
                            },
                        },
                        {
                            name: 'Fecha',
                            labelField: 'name',
                            valueField: 'id',
                            values: Object.keys(DateFilterOptions).map((k) => ({
                                id: DateFilterOptions[k],
                                name: DateFilterOptionsLabels[k],
                            })),
                            selectedValues: filters.date
                                ? [
                                      {
                                          name: `${getDateInDDMMYYYYFormat(
                                              filters.date.iniD
                                          )} ${t('hasta')} ${getDateInDDMMYYYYFormat(
                                              filters.date.endD
                                          )}`,
                                          id: null,
                                      },
                                  ]
                                : [],
                        },
                        {
                            name: 'Sucursal',
                            labelField: 'name',
                            valueField: 'id',
                            values: branches,
                            selectedValues: filters.branches,
                            hideSelection:
                                !!accountInfo?.branch ||
                                configs.systemBranch !==
                                    SystemBranchEnum.Branch,
                        },
                    ]}
                    search={search}
                    handleChangeSearch={(v) => {
                        setSearch(v)
                        if (v === search) return

                        clearTimeout(timeoutRef.current)
                        timeoutRef.current = setTimeout(() => {
                            getDocuments({ ...filters, page: 1 }, v)
                        }, 500)
                    }}
                    handleChangeSelectedFilter={(selectedFilter) => {
                        if (selectedFilter.name === 'Tipo de documento') {
                            const index = filters.documentTypes.findIndex(
                                (g) => g.id === selectedFilter.value.id
                            )
                            const newValues = [...filters.documentTypes]
                            if (selectedFilter.action === 'add')
                                newValues.push(selectedFilter.value)
                            else newValues.splice(index, 1)

                            setFilters({
                                ...filters,
                                documentTypes: newValues,
                                page: 1,
                            })
                            getDocuments(
                                {
                                    ...filters,
                                    documentTypes: newValues,
                                    page: 1,
                                },
                                search
                            )
                        }
                        if (selectedFilter.name === 'Usuário') {
                            const index = filters.accounts.findIndex(
                                (g) => g.id === selectedFilter.value.id
                            )
                            const newValues = [...filters.accounts]
                            if (selectedFilter.action === 'add')
                                newValues.push(selectedFilter.value)
                            else newValues.splice(index, 1)

                            setFilters({
                                ...filters,
                                accounts: newValues,
                                page: 1,
                            })
                            getDocuments(
                                {
                                    ...filters,
                                    accounts: newValues,
                                    page: 1,
                                },
                                search
                            )
                        }
                        if (selectedFilter.name === 'Fecha') {
                            if (
                                selectedFilter.value?.id ===
                                DateFilterOptions.Customize
                            ) {
                                setOpenDateModal(true)
                                return
                            }

                            const dates =
                                selectedFilter.value?.id &&
                                selectedFilter.action === 'add'
                                    ? getPresetDate(selectedFilter.value.id)
                                    : null

                            setFilters({
                                ...filters,
                                date: dates
                                    ? {
                                          iniD: dates?.firstDate,
                                          endD: dates?.lastDate,
                                      }
                                    : null,
                                page: 1,
                            })
                            getDocuments(
                                {
                                    ...filters,
                                    date: dates
                                        ? {
                                              iniD: dates?.firstDate,
                                              endD: dates?.lastDate,
                                          }
                                        : null,
                                    page: 1,
                                },
                                search
                            )
                        }
                        if (selectedFilter.name === 'Sucursal') {
                            const index = filters.branches.findIndex(
                                (g) => g.id === selectedFilter.value.id
                            )
                            const newValues = [...filters.branches]
                            if (selectedFilter.action === 'add')
                                newValues.push(selectedFilter.value)
                            else newValues.splice(index, 1)

                            setFilters({
                                ...filters,
                                branches: newValues,
                                page: 1,
                            })
                            getDocuments(
                                {
                                    ...filters,
                                    branches: newValues,
                                    page: 1,
                                },
                                search
                            )
                        }
                    }}
                    selectedValues={selectedDocuments.map(
                        (p) => p.id as number
                    )}
                    handleChangeSelectedValues={(values) => {
                        setSelectedDocuments(
                            values.map((value) => {
                                const foundValue = selectedDocuments.find(
                                    (sv) => sv.id === value
                                )
                                if (foundValue) return foundValue
                                else
                                    return documents.filter(
                                        (v) => v.id === value
                                    )[0]
                            })
                        )
                    }}
                    identifier="id"
                    values={documents}
                    pagination={{
                        page: filters.page,
                        rows: filters.rows,
                        rowsCount: filters.rowsCount,
                        handleChangePagination: (page, rows) => {
                            getDocuments(
                                { ...filters, page, rows },
                                search,
                                true
                            )
                        },
                    }}
                    columns={[
                        {
                            name: 'Tipo',
                            property: ['documentType', 'name'],
                            parser: (v) => t(v)
                        },
                        {
                            name: 'Fecha',
                            property: ['date'],
                            customRender: (document: DocumentInfo, value) => {
                                const text = getDateString({
                                    date: document.date ?? '',
                                    hour: document.hour ?? '',
                                    configs: configs,
                                    isSummerTime:
                                        document.isSummerTime ?? false,
                                })
                                const timeagoText = `${t('Hace')} ${getTimeAgo({
                                    date: `${document.date?.split('T')[0]} ${
                                        document.hour
                                    }`,
                                    configs: configs,
                                    isSummerTime:
                                        document.isSummerTime ?? false,
                                })}`

                                return (
                                    <Stack direction={'column'}>
                                        <Tooltip title={text}>
                                            <Typography
                                                
                                                fontSize={'0.875rem'}
                                            >
                                                {text}
                                            </Typography>
                                        </Tooltip>
                                        <Tooltip title={timeagoText}>
                                            <Typography
                                                
                                                fontSize={'0.75rem'}
                                                color={'#71797E'}
                                            >
                                                {timeagoText}
                                            </Typography>
                                        </Tooltip>
                                    </Stack>
                                )
                            },
                        },
                        {
                            name: 'Realizado por',
                            property: ['usuario'],
                            customRender: (document: DocumentInfo, value) => {
                                let text = document.account?.name ?? ''
                                if (document.cashier?.name)
                                    text += ` em ${document.cashier?.name}`

                                return (
                                    <Tooltip title={text}>
                                        <Typography
                                            
                                            fontSize={'0.875rem'}
                                        >
                                            {text}
                                        </Typography>
                                    </Tooltip>
                                )
                            },
                            hideSm: true,
                        },
                        {
                            name: 'Estado',
                            property: ['documentState'],
                            customRender: (document: DocumentInfo, value) => {
                                const color: Record<number, string> = {
                                    0: 'primary.main',
                                    1: 'success.main',
                                    2: 'error.main',
                                    3: 'error.main',
                                }
                                const bg: Record<number, string> = {
                                    0: 'rgb(0, 150, 255, 0.2)',
                                    1: 'rgb(124, 252, 0, 0.2)',
                                    2: 'rgb(255, 0, 0, 0.2)',
                                    3: 'rgb(255, 0, 0, 0.2)',
                                }

                                const text = t(DocumentStateLabel[value])

                                return (
                                    <Tooltip title={text}>
                                        <Box
                                            sx={{
                                                color: color[value],
                                                backgroundColor: bg[value],
                                                py: '5px',
                                                px: '12px',
                                                borderRadius: '6px',
                                            }}
                                        >
                                            {text}
                                        </Box>
                                    </Tooltip>
                                )
                            },
                            hideXs: true,
                        },
                        {
                            name: 'Archivo',
                            property: ['documentPath'],
                            customRender: (document: DocumentInfo, value) => {
                                return (
                                    <Tooltip title={value}>
                                        <Typography
                                            
                                            fontSize={'0.875rem'}
                                            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                                            onClick={
                                                () =>
                                                    downloadFile(
                                                        document.documentPath
                                                    )
                                                /* setDetailedDocument(document) */
                                            }
                                            color={'info.main'}
                                        >
                                            {value}
                                        </Typography>
                                    </Tooltip>
                                )
                            },
                        },
                        ...branchColumn,
                    ]}
                    actions={[
                        {
                            name: 'Gerar relatório geral',
                            color: 'success',
                            hide: !canGenerateGeneralDocument
                        },
                    ]}
                    handleExecuteAction={(action) => {
                        if (action === 'Gerar relatório geral') {
                            setOpenGeneralDataModal(true)
                        }
                    }}
                />
            </Box>
        </>
    )
}
